import { formatCurrency } from "@/lib/currency";
/**
 * Global Command Bar — search layer.
 *
 * Additive and read-only. Every query goes through the existing Supabase
 * client (so RLS/company scoping applies unchanged) and every result opens an
 * EXISTING route. No new pages, no schema changes, no permission changes.
 */
import { supabase } from "@/integrations/supabase/client";

export type CommandGroupId =
  | "navigation"
  | "jobs"
  | "customers"
  | "drivers"
  | "vehicles"
  | "invoices"
  | "appraisals"
  | "documents"
  | "tracking"
  | "messages"
  | "settings";

export interface CommandResult {
  id: string;
  group: CommandGroupId;
  title: string;
  subtitle?: string;
  badge?: string;
  /** Existing application route. Never a new page. */
  to: string;
  /** Extra terms used for client-side matching of static entries. */
  keywords?: string;
}

export interface CommandGroup {
  id: CommandGroupId;
  label: string;
  results: CommandResult[];
}

export interface CommandSearchContext {
  companyId: string | null;
  /** Drivers are isolated from financial data — enforced here and by RLS. */
  isDriver: boolean;
}

export const GROUP_LABELS: Record<CommandGroupId, string> = {
  navigation: "Go to",
  jobs: "Jobs",
  customers: "Customers",
  drivers: "Drivers",
  vehicles: "Vehicles",
  invoices: "Invoices",
  appraisals: "Appraisals",
  documents: "Documents",
  tracking: "Tracking",
  messages: "Messages",
  settings: "Settings",
};

/* ------------------------------------------------------------------ */
/* Static destinations (existing routes only)                          */
/* ------------------------------------------------------------------ */

interface StaticEntry extends Omit<CommandResult, "group"> {
  group: "navigation" | "settings";
  driverSafe: boolean;
}

const STATIC_ENTRIES: StaticEntry[] = [
  { id: "nav-dashboard", group: "navigation", title: "Dashboard", to: "/dashboard", keywords: "home overview", driverSafe: true },
  { id: "nav-operations", group: "navigation", title: "Operations Command Centre", to: "/operations", keywords: "dispatch live board", driverSafe: false },
  { id: "nav-jobs", group: "navigation", title: "Jobs", to: "/jobs", keywords: "job hub active", driverSafe: true },
  { id: "nav-pending", group: "navigation", title: "Pending Jobs", to: "/pending-jobs", keywords: "draft", driverSafe: true },
  { id: "nav-previous", group: "navigation", title: "Previous Jobs", to: "/previous-jobs", keywords: "completed history", driverSafe: true },
  { id: "nav-create-job", group: "navigation", title: "Create Job", to: "/create-job", keywords: "new job add", driverSafe: false },
  { id: "nav-schedule", group: "navigation", title: "Schedule", to: "/schedule", keywords: "calendar diary", driverSafe: true },
  { id: "nav-customers", group: "navigation", title: "Customers", to: "/customers", keywords: "crm clients accounts", driverSafe: false },
  { id: "nav-drivers", group: "navigation", title: "Driver Management", to: "/driver-management", keywords: "team staff", driverSafe: false },
  { id: "nav-tracking", group: "navigation", title: "Driver Tracking", to: "/driver-tracking", keywords: "map live location", driverSafe: false },
  { id: "nav-messages", group: "navigation", title: "Driver Messages", to: "/driver-messages", keywords: "chat sms notify", driverSafe: false },
  { id: "nav-my-messages", group: "navigation", title: "My Messages", to: "/my-messages", keywords: "inbox", driverSafe: true },
  { id: "nav-vehicles", group: "navigation", title: "Vehicles", to: "/vehicles", keywords: "fleet", driverSafe: true },
  { id: "nav-checks", group: "navigation", title: "Vehicle Daily Checks", to: "/vehicle-daily-checks", keywords: "walkaround defects", driverSafe: true },
  { id: "nav-invoicing", group: "navigation", title: "Invoicing", to: "/invoicing", keywords: "invoices billing", driverSafe: false },
  { id: "nav-ready-invoice", group: "navigation", title: "Ready to Invoice", to: "/ready-to-invoice", keywords: "billing queue", driverSafe: false },
  { id: "nav-quotes", group: "navigation", title: "Quote Builder", to: "/quote-builder", keywords: "quotation pricing", driverSafe: false },
  { id: "nav-expenses", group: "navigation", title: "Expenses", to: "/expenses", keywords: "costs receipts", driverSafe: false },
  { id: "nav-analytics", group: "navigation", title: "Analytics", to: "/analytics", keywords: "reports kpi", driverSafe: false },
  { id: "nav-setup", group: "navigation", title: "Setup Centre", to: "/setup-centre", keywords: "onboarding checklist", driverSafe: false },
  { id: "nav-help", group: "navigation", title: "Help Guides", to: "/help-guides", keywords: "support docs", driverSafe: true },

  { id: "set-settings", group: "settings", title: "Settings", to: "/settings", keywords: "company preferences configuration", driverSafe: false },
  { id: "set-users", group: "settings", title: "Manage Users", to: "/manage-users", keywords: "team permissions invite", driverSafe: false },
  { id: "set-subscription", group: "settings", title: "Subscription", to: "/subscription-status", keywords: "billing plan", driverSafe: false },
  { id: "set-plan", group: "settings", title: "Change Plan", to: "/change-plan", keywords: "upgrade downgrade", driverSafe: false },
  { id: "set-subcontractors", group: "settings", title: "Subcontractors", to: "/subcontractors", keywords: "partners", driverSafe: false },
  { id: "set-driver-settings", group: "settings", title: "Driver Settings", to: "/driver-settings", keywords: "profile", driverSafe: true },
];

export function staticResults(query: string, ctx: CommandSearchContext): CommandResult[] {
  const q = query.trim().toLowerCase();
  return STATIC_ENTRIES.filter((entry) => (ctx.isDriver ? entry.driverSafe : true))
    .filter((entry) =>
      !q || `${entry.title} ${entry.keywords ?? ""}`.toLowerCase().includes(q),
    )
    .map(({ driverSafe: _driverSafe, ...rest }) => rest as CommandResult);
}

/* ------------------------------------------------------------------ */
/* Remote search                                                       */
/* ------------------------------------------------------------------ */

const LIMIT = 6;
const esc = (value: string) => value.replace(/[%,()]/g, " ").trim();

const or = (columns: string[], term: string) =>
  columns.map((column) => `${column}.ilike.%${term}%`).join(",");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const scoped = (builder: any, companyId: string | null): any =>
  companyId ? builder.eq("company_id", companyId) : builder;

async function searchJobs(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data } = await scoped(
    supabase
      .from("jobs")
      .select(
        "id, job_reference, status, vehicle_make, vehicle_model, vehicle_registration, collection_address, delivery_address, customers!jobs_customer_id_fkey(name)",
      ),
    ctx.companyId,
  )
    .or(
      or(
        [
          "job_reference",
          "vehicle_registration",
          "vehicle_make",
          "vehicle_model",
          "collection_address",
          "delivery_address",
          "collection_postcode",
          "delivery_postcode",
        ],
        term,
      ),
    )
    .order("created_at", { ascending: false })
    .limit(LIMIT);

  return (data ?? []).map((job: Record<string, unknown>) => {
    const customer = job.customers as { name?: string } | null;
    return {
      id: `job-${job.id}`,
      group: "jobs" as const,
      title: (job.job_reference as string) || "Job",
      subtitle: [
        [job.vehicle_make, job.vehicle_model].filter(Boolean).join(" "),
        job.vehicle_registration as string,
        customer?.name,
      ]
        .filter(Boolean)
        .join(" · "),
      badge: (job.status as string) ?? undefined,
      to: `/job/${job.id}`,
    };
  });
}

async function searchTracking(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data } = await scoped(
    supabase
      .from("jobs")
      .select("id, job_reference, status, vehicle_registration, delivery_address")
      .in("status", ["collected", "in_transit", "arriving", "delivered"]),
    ctx.companyId,
  )
    .or(or(["job_reference", "vehicle_registration", "delivery_address"], term))
    .order("created_at", { ascending: false })
    .limit(4);

  return (data ?? []).map((job: Record<string, unknown>) => ({
    id: `track-${job.id}`,
    group: "tracking" as const,
    title: `Track ${job.job_reference ?? "job"}`,
    subtitle: [job.vehicle_registration, job.delivery_address].filter(Boolean).join(" · "),
    badge: job.status as string,
    to: `/track?jobId=${job.id}`,
  }));
}

async function searchCustomers(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data } = await scoped(
    supabase.from("customers").select("id, name, email, phone, postcode, customer_status"),
    ctx.companyId,
  )
    .or(or(["name", "email", "phone", "postcode"], term))
    .limit(LIMIT);

  return (data ?? []).map((c: Record<string, unknown>) => ({
    id: `customer-${c.id}`,
    group: "customers" as const,
    title: (c.name as string) || "Customer",
    subtitle: [c.email, c.phone, c.postcode].filter(Boolean).join(" · "),
    badge: (c.customer_status as string) ?? undefined,
    to: `/customers/${c.id}`,
  }));
}

async function searchDrivers(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data } = await scoped(
    supabase.from("drivers").select("id, name, email, phone, status, is_active"),
    ctx.companyId,
  )
    .or(or(["name", "email", "phone"], term))
    .limit(LIMIT);

  return (data ?? []).map((d: Record<string, unknown>) => ({
    id: `driver-${d.id}`,
    group: "drivers" as const,
    title: (d.name as string) || "Driver",
    subtitle: [d.email, d.phone].filter(Boolean).join(" · "),
    badge: d.is_active ? "Active" : (d.status as string) ?? undefined,
    to: "/driver-management",
  }));
}

async function searchVehicles(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data } = await scoped(
    supabase.from("vehicles").select("id, registration, make, model, color"),
    ctx.companyId,
  )
    .or(or(["registration", "make", "model", "color"], term))
    .limit(LIMIT);

  return (data ?? []).map((v: Record<string, unknown>) => ({
    id: `vehicle-${v.id}`,
    group: "vehicles" as const,
    title: (v.registration as string) || "Vehicle",
    subtitle: [v.make, v.model, v.color].filter(Boolean).join(" · "),
    to: "/vehicles",
  }));
}

async function searchInvoices(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const [jobInvoices, consolidated] = await Promise.all([
    scoped(
      supabase
        .from("jobs")
        .select("id, invoice_number, job_reference, payment_status, invoice_amount")
        .eq("invoice_generated", true),
      ctx.companyId,
    )
      .or(or(["invoice_number", "job_reference", "invoice_bill_to_name"], term))
      .limit(LIMIT),
    scoped(
      supabase
        .from("consolidated_invoices")
        .select("id, invoice_number, total_amount, payment_status, status"),
      ctx.companyId,
    )
      .or(or(["invoice_number"], term))
      .limit(4),
  ]);

  const results: CommandResult[] = (jobInvoices.data ?? []).map((j: Record<string, unknown>) => ({
    id: `invoice-job-${j.id}`,
    group: "invoices" as const,
    title: (j.invoice_number as string) || `Invoice for ${j.job_reference}`,
    subtitle: [j.job_reference, j.invoice_amount ? `${formatCurrency(Number(j.invoice_amount).toFixed(2))}` : null]
      .filter(Boolean)
      .join(" · "),
    badge: (j.payment_status as string) ?? undefined,
    to: `/invoice/${j.id}`,
  }));

  for (const inv of (consolidated.data ?? []) as Record<string, unknown>[]) {
    results.push({
      id: `invoice-consolidated-${inv.id}`,
      group: "invoices",
      title: (inv.invoice_number as string) || "Consolidated invoice",
      subtitle: inv.total_amount ? `${formatCurrency(Number(inv.total_amount).toFixed(2))} · Consolidated` : "Consolidated",
      badge: (inv.payment_status as string) || (inv.status as string) || undefined,
      to: "/invoicing",
    });
  }

  return results;
}

async function searchAppraisals(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data: jobs } = await scoped(
    supabase.from("jobs").select("id, job_reference, vehicle_registration"),
    ctx.companyId,
  )
    .or(or(["job_reference", "vehicle_registration"], term))
    .limit(5);

  const jobIds = (jobs ?? []).map((j: Record<string, unknown>) => j.id as string);
  if (!jobIds.length) return [];

  const { data } = await scoped(
    supabase
      .from("vehicle_appraisals")
      .select("id, job_id, appraisal_type, completed_at, created_at")
      .in("job_id", jobIds),
    ctx.companyId,
  )
    .order("created_at", { ascending: false })
    .limit(LIMIT);

  const byJob = new Map<string, Record<string, unknown>>(
    jobIds.map((id, i) => [id, (jobs?.[i] ?? {}) as Record<string, unknown>]),
  );

  return (data ?? []).map((a: Record<string, unknown>) => {
    const job = byJob.get(a.job_id as string);

    return {
      id: `appraisal-${a.id}`,
      group: "appraisals" as const,
      title: `${String(a.appraisal_type ?? "Appraisal").replace(/_/g, " ")} appraisal`,
      subtitle: [job?.job_reference, job?.vehicle_registration].filter(Boolean).join(" · "),
      badge: a.completed_at ? "Complete" : "In progress",
      to: `/appraisal/${a.id}`,
    };
  });
}

async function searchDocuments(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data } = await scoped(
    supabase
      .from("customer_documents")
      .select("id, customer_id, name, file_name, category")
      .order("created_at", { ascending: false }),
    ctx.companyId,
  )
    .or(or(["name", "file_name", "category"], term))
    .limit(LIMIT);

  return (data ?? []).map((d: Record<string, unknown>) => ({
    id: `document-${d.id}`,
    group: "documents" as const,
    title: (d.name as string) || (d.file_name as string) || "Document",
    subtitle: (d.category as string) ?? undefined,
    to: `/customers/${d.customer_id}?tab=documents`,
  }));
}

async function searchMessages(term: string, ctx: CommandSearchContext): Promise<CommandResult[]> {
  const { data } = await scoped(
    supabase
      .from("driver_messages")
      .select("id, title, body, created_at, drivers!driver_messages_driver_id_fkey(name)")
      .order("created_at", { ascending: false }),
    ctx.companyId,
  )
    .or(or(["title", "body"], term))
    .limit(LIMIT);

  return (data ?? []).map((m: Record<string, unknown>) => {
    const driver = m.drivers as { name?: string } | null;
    return {
      id: `message-${m.id}`,
      group: "messages" as const,
      title: (m.title as string) || "Message",
      subtitle: [driver?.name, (m.body as string)?.slice(0, 60)].filter(Boolean).join(" · "),
      to: ctx.isDriver ? "/my-messages" : "/driver-message-log",
    };
  });
}

/**
 * Runs every permitted source in parallel. A failing source never breaks the
 * command bar — it is simply omitted.
 */
export async function searchCommandBar(
  rawQuery: string,
  ctx: CommandSearchContext,
): Promise<CommandGroup[]> {
  const term = esc(rawQuery);
  const groups: CommandGroup[] = [];

  const navResults = staticResults(rawQuery, ctx);
  const push = (id: CommandGroupId, results: CommandResult[]) => {
    if (results.length) groups.push({ id, label: GROUP_LABELS[id], results });
  };

  if (term.length < 2) {
    push("navigation", navResults.filter((r) => r.group === "navigation").slice(0, 8));
    push("settings", navResults.filter((r) => r.group === "settings").slice(0, 4));
    return groups;
  }

  type Source = [CommandGroupId, Promise<CommandResult[]>];
  const sources: Source[] = [
    ["jobs", searchJobs(term, ctx)],
    ["tracking", searchTracking(term, ctx)],
    ["drivers", searchDrivers(term, ctx)],
    ["vehicles", searchVehicles(term, ctx)],
    ["appraisals", searchAppraisals(term, ctx)],
    ["messages", searchMessages(term, ctx)],
  ];

  // Drivers are isolated from customer/financial data.
  if (!ctx.isDriver) {
    sources.push(
      ["customers", searchCustomers(term, ctx)],
      ["invoices", searchInvoices(term, ctx)],
      ["documents", searchDocuments(term, ctx)],
    );
  }

  const settled = await Promise.allSettled(sources.map(([, promise]) => promise));

  const ordered: CommandGroupId[] = [
    "jobs",
    "customers",
    "drivers",
    "vehicles",
    "invoices",
    "appraisals",
    "documents",
    "tracking",
    "messages",
  ];

  const byGroup = new Map<CommandGroupId, CommandResult[]>();
  settled.forEach((outcome, index) => {
    if (outcome.status === "fulfilled" && outcome.value.length) {
      byGroup.set(sources[index][0], outcome.value);
    }
  });

  for (const id of ordered) push(id, byGroup.get(id) ?? []);

  push("navigation", navResults.filter((r) => r.group === "navigation").slice(0, 5));
  push("settings", navResults.filter((r) => r.group === "settings").slice(0, 4));

  return groups;
}
